import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';
import { mockData } from '../data/mock';

const HomePage = () => {
  const [stats, setStats] = useState(mockData.stats);
  const [featuredProjects, setFeaturedProjects] = useState(mockData.featuredProjects);

  useEffect(() => {
    setStats(mockData.stats);
    setFeaturedProjects(mockData.featuredProjects);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section with 3D Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* 3D Layered Background */}
        <div className="absolute inset-0">
          {/* Memorial Cylinder Background - Primary */}
          <div 
            className="absolute inset-0 opacity-80"
            style={{
              backgroundImage: "url('https://customer-assets.emergentagent.com/job_fenix-company/artifacts/igdfb98y_Image%2041.jpeg')",
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(3px)'
            }}
          ></div>
          
          {/* Gradient Overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/40 via-transparent to-emerald-900/30"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
            <span className="block text-white drop-shadow-2xl">Creating Beautiful Spaces for</span>
            <span className="block bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent drop-shadow-2xl">
              Reflection and Remembrance,
            </span>
            <span className="block bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent drop-shadow-2xl">
              for Farewell and Forever
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-10 max-w-4xl mx-auto leading-relaxed drop-shadow-xl">
            We are owners and developers of cemeteries and memorial gardens across the UK, dedicated to providing beautifully landscaped spaces where families can honour and remember their loved ones with dignity and peace. We create unparalleled memorial spaces that serve as <span className="text-amber-300 font-semibold">places for the living to remember, reflect, and find comfort</span> in beautifully designed environments that celebrate life.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link to="/masterplan">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-10 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300">
                The National Memorial Garden
                <ArrowRight className="ml-3" size={24} />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" size="lg" className="border-2 border-white/80 text-white hover:bg-white/20 px-10 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300">
                Our Development Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Comprehensive Services Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-emerald-50 to-teal-50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Memorial Solutions
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              We provide a complete range of memorial services with 24/7 accessibility, 
              particularly benefiting communities requiring immediate burial services within cultural or religious timeframes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Chapel Services & Ash Scattering</h3>
                <p className="text-gray-700 leading-relaxed">
                  Dignified ceremonies in peaceful surroundings with flexible 45-minute+ services, 
                  designed to honor your loved ones with the respect they deserve.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Memorial Tree Planting</h3>
                <p className="text-gray-700 leading-relaxed">
                  Living memorials with native species and personalised plaques, 
                  creating lasting tributes that grow and flourish over time.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Commemorative Plaques</h3>
                <p className="text-gray-700 leading-relaxed">
                  Customised memorials allowing freedom of expression in remembrance, 
                  crafted with the finest materials and attention to detail.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Columbarium Services</h3>
                <p className="text-gray-700 leading-relaxed">
                  Elegant resting places for urns with space for personal mementos, 
                  designed to provide comfort and a place for quiet reflection.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Digital Remembrance</h3>
                <p className="text-gray-700 leading-relaxed">
                  Modern commemoration options including online memorials, video tributes, 
                  photographs, and spoken word memories, creating lasting digital legacies for future generations.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 bg-white/90 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">24/7 Accessibility</h3>
                <p className="text-gray-700 leading-relaxed">
                  Our facilities operate around the clock, particularly benefiting communities 
                  requiring immediate burial services within cultural or religious timeframes.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What Makes Us Different - Rich Visual Section */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1725399463726-541e9acee536?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtZW1vcmlhbCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQyNzl8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 to-emerald-900/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Reimagining Memorial Spaces
            </h2>
            <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              We move beyond traditional cemetery designs to create landscapes that serve as 
              <span className="text-amber-300 font-semibold"> parks for the living</span>, 
              spaces where families can find peace, reflection, and celebration of life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Beautiful Landscapes Card */}
            <Card className="border-0 bg-white/95 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 overflow-hidden">
              <div 
                className="h-48 bg-cover bg-center relative"
                style={{
                  backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/lvrtfpku_Image%2058.jpeg')"
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white drop-shadow-lg">Beautiful Landscapes</h3>
                </div>
              </div>
              <CardContent className="p-8">
                <p className="text-gray-700 leading-relaxed">
                  We design memorial gardens as living parks with diverse botanical areas, 
                  tranquil water features, and sculptural elements that create atmospheres of reflection.
                </p>
              </CardContent>
            </Card>

            {/* Development Excellence Card */}
            <Card className="border-0 bg-white/95 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 overflow-hidden">
              <div 
                className="h-48 bg-cover bg-center relative"
                style={{
                  backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/20ee5ffc_Picture%209.jpg')"
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white drop-shadow-lg">Development Excellence</h3>
                </div>
              </div>
              <CardContent className="p-8">
                <p className="text-gray-700 leading-relaxed">
                  As owners and developers, we focus on creating exceptional facilities from conception 
                  to completion, then partnering with expert management teams for ongoing operations.
                </p>
              </CardContent>
            </Card>

            {/* Compassionate Design Card */}
            <Card className="border-0 bg-white/95 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 overflow-hidden">
              <div 
                className="h-48 bg-cover bg-center relative"
                style={{
                  backgroundImage: "url('https://customer-assets.emergentagent.com/job_memorial-gardens/artifacts/9hzkyna9_Image%2046.jpeg')"
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white drop-shadow-lg">Compassionate Design</h3>
                </div>
              </div>
              <CardContent className="p-8">
                <p className="text-gray-700 leading-relaxed">
                  Every space we create is designed with families in mind, providing dignity, 
                  peace, and beautiful environments where memories can be honored and celebrated.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The National Memorial Garden - Our Next Chapter */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1725399463726-541e9acee536?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtZW1vcmlhbCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQyNzl8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 to-emerald-900/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">The National Memorial Garden</h2>
            <h3 className="text-2xl md:text-3xl font-semibold text-amber-300 mb-8">Our Next Chapter</h3>
            <p className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              We are excited to announce our upcoming flagship development: The National Memorial Garden 
              in Great Hallingbury, Hertfordshire - a groundbreaking UK-wide memorial facility.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h4 className="text-xl font-bold text-white mb-4">Strategic Location</h4>
              <p className="text-white/90 mb-6 leading-relaxed">
                Positioned near the M11, M25, and M1 motorways for exceptional accessibility across the UK, 
                creating a new benchmark in memorial services.
              </p>
            </div>
            
            <Card className="bg-white/95 backdrop-blur-sm shadow-2xl">
              <CardContent className="p-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Innovative Design Features</h4>
                <div className="space-y-4">
                  {[
                    "Contemporary chapel and service buildings",
                    "Diverse botanical areas and tranquil water features", 
                    "Sculptural elements creating atmospheres of reflection",
                    "Private meeting rooms and family facilities",
                    "On-site café, florist, and comprehensive visitor amenities",
                    "Advanced accessibility features including mobility aids and wheelchair-friendly design"
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Link to="/masterplan">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300">
                Explore The National Memorial Garden
                <ArrowRight className="ml-3" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section with 3D Background */}
      <section className="py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1544862870-a4d5e74dc313?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxwZWFjZWZ1bCUyMGdhcmRlbnxlbnwwfHx8fDE3NTYxMjQzMDN8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-amber-900/90"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="backdrop-blur-sm bg-white/10 rounded-3xl p-12 border border-white/20 shadow-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Ready to Create Something Beautiful?</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Discover how we're transforming memorial spaces across the UK with innovative design 
              and compassionate development that celebrates life.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold shadow-2xl transform hover:scale-105 transition-all duration-300">
                  Get In Touch
                </Button>
              </Link>
              <Link to="/masterplan">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-4 text-lg font-semibold backdrop-blur-sm shadow-2xl transform hover:scale-105 transition-all duration-300">
                  View The Masterplan
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;